import { onMount, type JSX } from 'solid-js';

export interface ReusableStylesheet {
  readonly key: string;
  readonly cssText: string;
  readonly sheet?: CSSStyleSheet;
}

const adoptedFallbackKeys = new WeakMap<Document | ShadowRoot, Set<string>>();
const reusableStylesheetKeyProperty = '__trimbleReusableStylesheetKey';
const reusableStylesheetSignatureProperty =
  '__trimbleReusableStylesheetSignature';

interface KeyedCSSStyleSheet extends CSSStyleSheet {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __trimbleReusableStylesheetKey?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __trimbleReusableStylesheetSignature?: string;
}

export function createReusableStylesheet(
  key: string,
  cssText: string
): ReusableStylesheet {
  const sheet = createConstructableStylesheet(cssText);

  if (sheet) {
    markStylesheet(sheet, key);
    return { key, cssText, sheet };
  }

  return { key, cssText };
}

export function adoptReusableStylesheets(
  root: Document | ShadowRoot,
  stylesheets: readonly ReusableStylesheet[]
): void {
  const constructableStylesheets = stylesheets.flatMap((stylesheet) =>
    stylesheet.sheet ? [stylesheet.sheet] : []
  );

  if (constructableStylesheets.length > 0 && 'adoptedStyleSheets' in root) {
    const current = root.adoptedStyleSheets;
    const constructableStylesheetKeys = new Set(
      constructableStylesheets
        .map((stylesheet) => getStylesheetKey(stylesheet))
        .filter((key): key is string => key !== undefined)
    );
    const constructableStylesheetSignatures = new Set(
      constructableStylesheets
        .map((stylesheet) => getStylesheetSignature(stylesheet))
        .filter((signature): signature is string => signature !== undefined)
    );
    const retained = current.filter((stylesheet) => {
      const stylesheetKey = getStylesheetKey(stylesheet);
      const stylesheetSignature = getStylesheetSignature(stylesheet);

      return (
        !constructableStylesheets.includes(stylesheet) &&
        (stylesheetKey === undefined ||
          !constructableStylesheetKeys.has(stylesheetKey)) &&
        (stylesheetSignature === undefined ||
          !constructableStylesheetSignatures.has(stylesheetSignature))
      );
    });

    root.adoptedStyleSheets = [...retained, ...constructableStylesheets];
  }

  for (const stylesheet of stylesheets) {
    if (stylesheet.sheet && 'adoptedStyleSheets' in root) {
      continue;
    }

    appendFallbackStyle(root, stylesheet);
  }
}

export function AdoptReusableStylesheets(props: {
  readonly stylesheets: readonly ReusableStylesheet[];
}): JSX.Element {
  if (typeof document === 'undefined') {
    return null;
  }

  const marker = document.createElement('span');
  marker.style.display = 'none';

  onMount(() => {
    const root = marker.getRootNode();

    if (isStyleRoot(root)) {
      adoptReusableStylesheets(root, props.stylesheets);
    }
  });

  return marker;
}

function createConstructableStylesheet(
  cssText: string
): CSSStyleSheet | undefined {
  if (typeof CSSStyleSheet === 'undefined' || cssText.includes('@import')) {
    return undefined;
  }

  const stylesheet = new CSSStyleSheet();

  try {
    stylesheet.replaceSync(cssText);
  } catch {
    return undefined;
  }

  return stylesheet;
}

function markStylesheet(stylesheet: CSSStyleSheet, key: string): void {
  const signature = getStylesheetSignature(stylesheet);

  try {
    Object.defineProperty(stylesheet, reusableStylesheetKeyProperty, {
      configurable: true,
      value: key,
    });

    if (signature !== undefined) {
      Object.defineProperty(stylesheet, reusableStylesheetSignatureProperty, {
        configurable: true,
        value: signature,
      });
    }
  } catch {
    (stylesheet as KeyedCSSStyleSheet)[reusableStylesheetKeyProperty] = key;

    if (signature !== undefined) {
      (stylesheet as KeyedCSSStyleSheet)[reusableStylesheetSignatureProperty] =
        signature;
    }
  }
}

function getStylesheetKey(stylesheet: CSSStyleSheet): string | undefined {
  return (stylesheet as KeyedCSSStyleSheet)[reusableStylesheetKeyProperty];
}

function getStylesheetSignature(stylesheet: CSSStyleSheet): string | undefined {
  const markedSignature = (stylesheet as KeyedCSSStyleSheet)[
    reusableStylesheetSignatureProperty
  ];

  if (markedSignature !== undefined) {
    return markedSignature;
  }

  try {
    const rules = Array.from(stylesheet.cssRules);
    const rulesText = rules.map((rule) => rule.cssText).join('\n');

    return `${String(rules.length)}:${rulesText}`;
  } catch {
    return undefined;
  }
}

function appendFallbackStyle(
  root: Document | ShadowRoot,
  stylesheet: ReusableStylesheet
): void {
  const adoptedKeys = getFallbackKeys(root);
  const host = getFallbackStyleHost(root);
  const existingStyle = getFallbackStyleElement(host, stylesheet.key);

  if (existingStyle) {
    host.appendChild(existingStyle);
    adoptedKeys.add(stylesheet.key);
    return;
  }

  if (adoptedKeys.has(stylesheet.key)) {
    return;
  }

  const style = document.createElement('style');
  style.dataset['trimbleReusableStylesheet'] = stylesheet.key;
  style.textContent = stylesheet.cssText;
  host.appendChild(style);
  adoptedKeys.add(stylesheet.key);
}

function getFallbackStyleElement(
  host: Node,
  key: string
): HTMLStyleElement | undefined {
  if (
    !(
      host instanceof Document ||
      host instanceof ShadowRoot ||
      host instanceof Element
    )
  ) {
    return undefined;
  }

  return (
    Array.from(
      host.querySelectorAll<HTMLStyleElement>(
        'style[data-trimble-reusable-stylesheet]'
      )
    ).find((style) => style.dataset['trimbleReusableStylesheet'] === key) ??
    undefined
  );
}

function getFallbackKeys(root: Document | ShadowRoot): Set<string> {
  const existing = adoptedFallbackKeys.get(root);

  if (existing) {
    return existing;
  }

  const next = new Set<string>();
  adoptedFallbackKeys.set(root, next);
  return next;
}

function getFallbackStyleHost(root: Document | ShadowRoot): Node {
  if ('head' in root) {
    return root.head;
  }

  return root;
}

function isStyleRoot(root: Node): root is Document | ShadowRoot {
  return root instanceof Document || root instanceof ShadowRoot;
}
